import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider.jsx";
import { canManage } from "../lib/roles.js";
import { useActiveStore } from "../lib/activeStore.js";
import { useFeatureEnabled } from "../lib/appConfig.js";
import { Sparkline } from "../components/Sparkline.jsx";
import {
  subscribeUnits, subscribeRecentSnapshots, subscribeStore,
  riskScore, isCheckStale, relativeTime,
  chargeCycleStatus, needsCharge, daysSinceCheck, CHARGE_CYCLE,
} from "../lib/data.js";

// Phase 1b — the new car inventory manager view.
// Count headline + concrete breakdown. No literary chrome. Critical and
// warning rows pin to top with the same colored rail used on the queue cards.
export function Manager() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [units, setUnits] = useState([]);
  const [error, setError] = useState(null);

  const activeStore = useActiveStore();
  const weeklyReportEnabled = useFeatureEnabled("weeklyReport");
  const [snapshots, setSnapshots] = useState([]);

  // Subscribe to the store doc so we can show the HUMAN display name in the
  // header — never the raw slug. Falls back to the slug only if the store
  // record has no `name` / `displayName` field set yet.
  const [storeDoc, setStoreDoc] = useState(null);
  useEffect(() => {
    if (!activeStore) return;
    return subscribeStore(activeStore, (s) => setStoreDoc(s));
  }, [activeStore]);
  const storeLabel = storeDoc?.displayName || storeDoc?.name || activeStore || "—";

  // Collapsible list toggles — start collapsed so the page reads cleanly,
  // expand on demand. Keeps the eye out of a 100-row scroll.
  const [showAllInventory, setShowAllInventory] = useState(false);
  const INVENTORY_PREVIEW = 12;
  useEffect(() => {
    if (!profile?.tenantId || !activeStore) return;
    return subscribeUnits(
      { tenantId: profile.tenantId, storeId: activeStore },
      (u, err) => { if (err) setError(err); else { setUnits(u); setError(null); } },
    );
  }, [profile?.tenantId, activeStore]);

  // Compliance trend — last 28 days of snapshots, sparkline above the KPIs.
  // Empty until the batterySnapshotDaily Cloud Function has run at least once.
  useEffect(() => {
    if (!profile?.tenantId || !activeStore) return;
    return subscribeRecentSnapshots(
      { tenantId: profile.tenantId, storeId: activeStore, days: 28 },
      (rows) => setSnapshots(rows || []),
    );
  }, [profile?.tenantId, activeStore]);

  const ranked = useMemo(
    () => [...units].map((u) => ({ ...u, _risk: riskScore(u) })).sort((a, b) => b._risk - a._risk),
    [units],
  );

  const stats = useMemo(() => {
    let due = 0, overdue = 0, unchecked = 0;
    let healthyN = 0, lowN = 0, criticalN = 0;
    let vSum = 0, vCount = 0;
    for (const u of units) {
      const cc = chargeCycleStatus(u);
      if (cc === "due")     due++;
      if (cc === "overdue") overdue++;
      if (daysSinceCheck(u) == null) unchecked++;
      if (typeof u.voltage === "number") {
        vSum += u.voltage; vCount++;
        if (u.voltage >= 12.5) healthyN++;
        else if (u.voltage >= 12.2) lowN++;
        else criticalN++;
      } else {
        if (cc === "overdue") criticalN++;
        else if (cc === "due") lowN++;
        else healthyN++;
      }
    }
    const total = units.length;
    const inCycle = total - (due + overdue);
    const compliancePct = total > 0 ? Math.round((inCycle / total) * 100) : 100;
    const attention = due + overdue;
    const avgVoltage = vCount ? +(vSum / vCount).toFixed(2) : null;
    return {
      due, overdue, unchecked,
      total, inCycle, compliancePct,
      needsCharge: due + overdue,
      attention,
      fleetHealth: { healthy: healthyN, low: lowN, critical: criticalN },
      avgVoltage,
    };
  }, [units]);

  // "At risk for delivery this week" — anything that's overdue or due
  // for a charge cycle. Strictly time-based; same gating as the lot walk.
  const atRiskForDelivery = useMemo(
    () => ranked.filter((u) => {
      const cc = chargeCycleStatus(u);
      return cc === "overdue" || cc === "due";
    }),
    [ranked],
  );

  if (!canManage(profile)) {
    return (
      <main className="container">
        <div className="panel" style={{ maxWidth: 480, margin: "60px auto" }}>
          <div className="eyebrow">Restricted</div>
          <h2 style={{ marginTop: 10 }}>Manager view is admin-only.</h2>
          <p style={{ marginTop: 10 }}>
            Ask a store admin to grant you the manager role on Battery Care if you need access.
          </p>
          <Link to="/" className="btn" style={{ marginTop: 14 }}>← Home</Link>
        </div>
      </main>
    );
  }

  return (
    <main className="today-stage">
      {/* Editorial header — matches the rest of Battery Care. Eyebrow with
          context, single declarative Crimson Pro line, dense KPI sub-line,
          actions on the right. Same visual grammar as Today / Walk. */}
      <header className="today-head today-head-mini">
        <div className="today-greet">
          <div className="today-eye">
            <span className="today-eye-dot" />
            MANAGER · {storeLabel.toUpperCase()}
          </div>
          <h1 className="today-h1">
            {stats.attention === 0
              ? <>Lot is <em>clean</em>.</>
              : <>{stats.attention} unit{stats.attention === 1 ? "" : "s"} need{stats.attention === 1 ? "s" : ""} <em>a charge</em>.</>
            }
          </h1>
          <p className="today-sub">
            <b style={{ color: "#7C2935" }}>{stats.overdue} overdue</b>
            {" · "}
            <b style={{ color: "#B05E13" }}>{stats.due} due soon</b>
            {stats.unchecked > 0 && (<> · <b style={{ color: "var(--text-dim, #6A6D74)" }}>{stats.unchecked} never checked</b></>)}
            <span style={{ color: "var(--text-dim, #6A6D74)", marginLeft: 8 }}>
              · {stats.total} total · {CHARGE_CYCLE.overdueDays}d cycle
            </span>
          </p>
        </div>
        <div className="mgr-actions">
          {weeklyReportEnabled && (
            <a
              href="/report"
              target="_blank"
              rel="noopener noreferrer"
              className="dash-tb-btn ghost"
            >
              Weekly report
            </a>
          )}
          <Link to="/import" className="dash-tb-btn ghost">Import DAR</Link>
          <Link to="/units/new" className="dash-tb-btn primary">+ Add unit</Link>
        </div>
      </header>

      {error && (
        <div className="w-shell" style={{ marginTop: 18, borderColor: "#7C2935", color: "#7C2935", padding: "16px 20px" }}>
          Couldn't load inventory: {error.message || String(error)}
        </div>
      )}

      {/* =========================================================
          KPI TILES — four editorial cards, same shape as widgets.
          Compliance leads, then overdue / due / total.
          ========================================================= */}
      <div className="mgr-stats">
        <div className="w-shell mgr-stat">
          <div className="w-eye"><span className="w-eye-dot" />COMPLIANCE</div>
          <div className="mgr-stat-num" style={{ color: stats.compliancePct >= 90 ? "#2A6D38" : stats.compliancePct >= 70 ? "#B05E13" : "#7C2935" }}>
            {stats.compliancePct}<span className="mgr-stat-unit">%</span>
          </div>
          <div className="mgr-stat-sub">{stats.inCycle} of {stats.total} on schedule</div>
        </div>
        <div className="w-shell mgr-stat">
          <div className="w-eye"><span className="w-eye-dot" />OVERDUE</div>
          <div className="mgr-stat-num" style={{ color: stats.overdue > 0 ? "#7C2935" : "var(--text, #14161A)" }}>
            {stats.overdue}
          </div>
          <div className="mgr-stat-sub">&gt; {CHARGE_CYCLE.overdueDays} days since charge</div>
        </div>
        <div className="w-shell mgr-stat">
          <div className="w-eye"><span className="w-eye-dot" />DUE SOON</div>
          <div className="mgr-stat-num" style={{ color: stats.due > 0 ? "#B05E13" : "var(--text, #14161A)" }}>
            {stats.due}
          </div>
          <div className="mgr-stat-sub">{CHARGE_CYCLE.dueDays}–{CHARGE_CYCLE.overdueDays} days</div>
        </div>
        <div className="w-shell mgr-stat">
          <div className="w-eye"><span className="w-eye-dot" />ACTIVE UNITS</div>
          <div className="mgr-stat-num">{stats.total}</div>
          <div className="mgr-stat-sub">in the lot</div>
        </div>
      </div>

      {/* =========================================================
          FLEET VITALS + TREND — two clean editorial cards.
          Vitals: segmented bar showing healthy/low/critical proportion,
          counts beneath, optional voltage + cycle line.
          Trend: same as before, just typography (no cramped circles).
          ========================================================= */}
      {stats.total > 0 && (
        <div className="mgr-row">
          <div className="w-shell mgr-vitals">
            <div className="w-eye"><span className="w-eye-dot" />FLEET VITALS</div>
            <h3 className="w-h3">
              <em>{stats.fleetHealth.healthy}</em> of {stats.total} healthy
              {stats.fleetHealth.critical > 0 && (<>, <em style={{ color: "#7C2935" }}>{stats.fleetHealth.critical} critical</em></>)}.
            </h3>

            {/* Segmented bar — proportional healthy / low / critical */}
            {(() => {
              const { healthy, low, critical } = stats.fleetHealth;
              const total = Math.max(1, healthy + low + critical);
              const pct = (n) => Math.max(0, (n / total) * 100);
              return (
                <>
                  <div className="mgr-vbar" role="img" aria-label="Fleet health breakdown">
                    <span className="mgr-vbar-seg ok"  style={{ width: pct(healthy)  + "%" }} title={`${healthy} healthy`} />
                    <span className="mgr-vbar-seg warn" style={{ width: pct(low)      + "%" }} title={`${low} low`} />
                    <span className="mgr-vbar-seg over" style={{ width: pct(critical) + "%" }} title={`${critical} critical`} />
                  </div>
                  <div className="mgr-vbar-legend">
                    <span><i className="dot ok"   /> {healthy} healthy</span>
                    <span><i className="dot warn" /> {low} low</span>
                    <span><i className="dot over" /> {critical} critical</span>
                  </div>
                </>
              );
            })()}

            <div className="mgr-vitals-foot">
              {stats.avgVoltage != null && (
                <div className="mgr-vitals-stat">
                  <div className="mgr-vitals-stat-label">Avg voltage</div>
                  <div className="mgr-vitals-stat-val">
                    {stats.avgVoltage.toFixed(2)}<span className="mgr-vitals-stat-unit">V</span>
                  </div>
                </div>
              )}
              <div className="mgr-vitals-stat">
                <div className="mgr-vitals-stat-label">Cycle target</div>
                <div className="mgr-vitals-stat-val">{CHARGE_CYCLE.overdueDays}<span className="mgr-vitals-stat-unit">d</span></div>
              </div>
              <div className="mgr-vitals-stat">
                <div className="mgr-vitals-stat-label">In-cycle</div>
                <div className="mgr-vitals-stat-val">{stats.compliancePct}<span className="mgr-vitals-stat-unit">%</span></div>
              </div>
            </div>
          </div>

          <div className="w-shell mgr-trend">
            <div className="w-eye"><span className="w-eye-dot" />28-DAY TREND</div>
            {snapshots.length >= 2 ? (
              <>
                {(() => {
                  const last = snapshots[snapshots.length - 1]?.compliancePct ?? 0;
                  const first = snapshots[0]?.compliancePct ?? 0;
                  const delta = last - first;
                  const arrow = delta > 0 ? "↑" : delta < 0 ? "↓" : "→";
                  const color = delta > 0 ? "#2A6D38" : delta < 0 ? "#7C2935" : "var(--text-dim, #6A6D74)";
                  return (
                    <>
                      <h3 className="w-h3"><em>{last}%</em> compliance.</h3>
                      <div style={{ fontSize: 14, color, marginTop: 4, marginBottom: 14 }}>
                        {arrow} {delta >= 0 ? "+" : ""}{delta}pp <span style={{ color: "var(--text-dim, #6A6D74)" }}>vs 4 wks ago</span>
                      </div>
                    </>
                  );
                })()}
                <div style={{ background: "var(--bg, #F6F6F3)", border: "1px solid var(--line, rgba(18,20,22,0.07))", borderRadius: 10, padding: "14px 16px" }}>
                  <Sparkline values={snapshots.map((s) => s.compliancePct ?? 0)} height={64} />
                </div>
              </>
            ) : (
              <>
                <h3 className="w-h3">Trend <em>building</em>.</h3>
                <p style={{ fontSize: 14, color: "var(--text-muted, #4A4D54)", marginTop: 10, lineHeight: 1.55 }}>
                  Need at least two daily snapshots before SEVRIC can chart the trend. Check back tomorrow.
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {/* =========================================================
          THIS WEEK'S WALK — at-risk units in the same .w-list pattern
          as the dashboard widgets. Click row → /check/:id.
          ========================================================= */}
      <section className="mgr-section">
        <header className="mgr-section-head">
          <div>
            <div className="mgr-cap">THIS WEEK'S WALK</div>
            <h2 className="mgr-h2">
              {atRiskForDelivery.length === 0
                ? <>Nothing <em>due</em>.</>
                : <>{atRiskForDelivery.length} unit{atRiskForDelivery.length === 1 ? "" : "s"} need <em>a charge</em>.</>
              }
            </h2>
          </div>
          {atRiskForDelivery.length > 0 && (
            <Link to="/walk" className="dash-tb-btn primary" style={{ textDecoration: "none" }}>Start a walk →</Link>
          )}
        </header>

        {atRiskForDelivery.length === 0 ? (
          <div className="w-shell" style={{ padding: "22px 24px", color: "#2A6D38" }}>
            ✓ Nothing to flag. The line is clean.
          </div>
        ) : (
          <div className="w-list">
            {atRiskForDelivery.map((u) => {
              const cc = chargeCycleStatus(u);
              const ds = daysSinceCheck(u);
              const status = cc === "overdue" ? "over" : cc === "due" ? "warn" : "";
              const tag = cc === "overdue" ? "Overdue" : cc === "due" ? "Due soon" : "—";
              const subject = [u.year, u.make, u.model, u.trim].filter(Boolean).join(" ") || "—";
              return (
                <button
                  key={u.id}
                  type="button"
                  className={`w-row${status ? " " + status : ""}`}
                  style={{ gridTemplateColumns: "30px 1fr 80px 90px" }}
                  onClick={() => navigate(`/check/${u.id}`)}
                >
                  <div className="w-row-av">
                    <svg width="22" height="14" viewBox="0 0 40 20" aria-hidden="true">
                      <path d="M 4 16 L 8 9 L 14 7 L 30 7 L 34 9 L 36 14 L 38 16 L 6 16 Z" fill="currentColor" opacity="0.18" stroke="currentColor" strokeWidth="0.7" />
                      <circle cx="12" cy="16" r="2" fill="none" stroke="currentColor" strokeWidth="0.7" />
                      <circle cx="30" cy="16" r="2" fill="none" stroke="currentColor" strokeWidth="0.7" />
                    </svg>
                  </div>
                  <div className="w-row-main">
                    <div className="w-row-name">{subject}</div>
                    <div className="w-row-sub">
                      {u.stockNumber || "—"} · {u.daysOnLot ?? 0}d on lot · {ds != null ? `${ds}d since charge` : "never charged"}
                    </div>
                  </div>
                  <div className="w-row-age">{ds != null ? `${ds}d` : "—"}</div>
                  <div className="w-row-tag"><span className={`w-pill${status ? " " + status : ""}`}>{tag}</span></div>
                </button>
              );
            })}
          </div>
        )}
      </section>

      {/* =========================================================
          FULL INVENTORY — every active unit, risk-ranked. Defaults to
          a 12-row preview so the page doesn't scroll forever.
          "Show all" expands. Inventory page handles deeper search.
          ========================================================= */}
      <section className="mgr-section">
        <header className="mgr-section-head">
          <div>
            <div className="mgr-cap">FULL INVENTORY · {ranked.length} ACTIVE</div>
            <h2 className="mgr-h2">All units, <em>risk-ranked</em>.</h2>
          </div>
          <Link to="/inventory" className="dash-tb-btn ghost" style={{ textDecoration: "none" }}>Open inventory →</Link>
        </header>

        {ranked.length === 0 ? (
          <div className="w-shell" style={{ padding: "32px 24px", textAlign: "center", color: "var(--text-dim, #6A6D74)" }}>
            No units yet. Tap + Add unit above to start.
          </div>
        ) : (
          <>
            <div className="w-list">
              {(showAllInventory ? ranked : ranked.slice(0, INVENTORY_PREVIEW)).map((u) => {
                const cc = chargeCycleStatus(u);
                const ds = daysSinceCheck(u);
                const status = cc === "overdue" ? "over" : cc === "due" ? "warn" : "";
                const tag = cc === "overdue" ? "Overdue" : cc === "due" ? "Due soon" : cc === "new" ? "New" : "OK";
                const subject = [u.year, u.make, u.model, u.trim].filter(Boolean).join(" ") || "—";
                return (
                  <button
                    key={u.id}
                    type="button"
                    className={`w-row${status ? " " + status : ""}`}
                    style={{ gridTemplateColumns: "30px 1fr 70px 70px 80px" }}
                    onClick={() => navigate(`/check/${u.id}`)}
                  >
                    <div className="w-row-av">
                      <svg width="22" height="14" viewBox="0 0 40 20" aria-hidden="true">
                        <path d="M 4 16 L 8 9 L 14 7 L 30 7 L 34 9 L 36 14 L 38 16 L 6 16 Z" fill="currentColor" opacity="0.18" stroke="currentColor" strokeWidth="0.7" />
                        <circle cx="12" cy="16" r="2" fill="none" stroke="currentColor" strokeWidth="0.7" />
                        <circle cx="30" cy="16" r="2" fill="none" stroke="currentColor" strokeWidth="0.7" />
                      </svg>
                    </div>
                    <div className="w-row-main">
                      <div className="w-row-name">{subject}</div>
                      <div className="w-row-sub">
                        {u.stockNumber || "—"} · {u.department || "—"} · risk {u._risk}
                      </div>
                    </div>
                    <div className="w-row-age">{u.daysOnLot ?? 0}d</div>
                    <div className="w-row-age">{ds != null ? `${ds}d` : "—"}</div>
                    <div className="w-row-tag"><span className={`w-pill${status ? " " + status : ""}`}>{tag}</span></div>
                  </button>
                );
              })}
            </div>
            {ranked.length > INVENTORY_PREVIEW && (
              <div className="mgr-list-toggle">
                <button
                  type="button"
                  className="dash-tb-btn ghost"
                  onClick={() => setShowAllInventory((v) => !v)}
                >
                  {showAllInventory
                    ? `Show top ${INVENTORY_PREVIEW} only`
                    : `Show all ${ranked.length} units →`}
                </button>
              </div>
            )}
          </>
        )}
      </section>

    </main>
  );
}
